import { accountsEnvironment } from '../accounts-environment';
import { requiredHeaders } from '../../../models/required-headers';
import { selectData } from '../../../utils/select-data';
import { withJwt } from '../../../utils/with-jwt';
import { companySchema } from '../schemas/company.schema';
import { companyInfoSchema } from '../schemas/company-info.schema';
import { coercedDate } from '../../../utils/coerced-date';
import { fromFetch } from 'rxjs/fetch';
import { z } from 'zod/v4';

const requestSchema = companySchema
  .omit({
    alias: true,
    products: true,
    purchaseOrderHistory: true,
  })
  .partial()
  .extend({
    companyInfo: companyInfoSchema
      .partial()
      .extend({
        contactName: z.string(),
        email: z.string(),
        phone: z.string(),
      })
      .required({
        contactName: true,
        email: true,
        phone: true,
      }),
  })
  .required({
    name: true,
    accountType: true,
    companyInfo: true,
  });

/**
 * Used by a Trimble Maps partner to update an account.
 * @param alias Account Alias
 * @param company Updated account details
 * @returns Basic info for updated account
 *
 * @example
 * AccountsApi.putCompany({
 *  name: 'Account Name',
 *  accountType: 'Paying Customer',
 *  companyInfo: {
 *    contactName: 'Steve',
 *    email: 'email@example.com'
 *    phone: '555-555-5555
 *  }
 * })t
 */
export function putCompany(
  this: unknown,
  alias: string,
  company: z.infer<typeof requestSchema>
) {
  const params = new URLSearchParams([['alias', alias]]);
  return withJwt((jwtAuthHeaders) =>
    fromFetch(
      new Request(
        `${accountsEnvironment.resolve(this).companyUrl}?${params.toString()}`,
        {
          method: 'PUT',
          headers: {
            ...requiredHeaders,
            ...jwtAuthHeaders,
          },
          body: JSON.stringify(company),
        }
      )
    )
  ).pipe(
    selectData(
      z.object({
        accountId: z.number().min(1),
        alias: z.string().min(1),
        success: z.boolean(),
        modifiedOn: coercedDate,
      })
    )
  );
}
